/**
 * Iter-193 — "Replacement door — scope of works" wording for quotes.
 *
 * Whenever a quote carries a door-replacement line the quote (PDF + client
 * portal) prints the full scope the price allows for, so the client knows
 * exactly what "Supply & fit new FD30s door and frame" covers. The company
 * can re-word it once in Settings → Quote Template (`quote_replacement_scope`);
 * blank = the standard wording below.
 */

export const REPLACEMENT_SCOPE_DEFAULT: string = [
  'Supply FD30s door and frame – Price allows for non-standard door size; standard doors have a maximum trimming allowance, so may need to be flush/blank solid door to accommodate irregular sizes, and avoid a complete bespoke order (triple the price and 10-14 weeks lead time)',
  'Supply additional correct density timber – To allow for new frame being narrower than existing',
  'Frame removal – Remove architraves (and retain), remove the door and frame, remove packing materials.',
  'Remove from site and dispose of old door, frame and ironmongery',
  'Supply new fire rated ironmongery – To ensure installation is fully compliant',
  'Route, chisel and prepare door for ironmongery – Ensure all items installed with necessary intumescent packers, wraps and covers',
  'Frame installation',
  'Hang the new door, fire stop and seal around the frame with compliant products, record the work for compliance, then replace architraves and caulk.',
  'Supply and install fire rated overhead closer',
  'Supply and install fire rated sash lock with euro thumbturn cylinder',
  'Supply and install fire rated intumescent dropdown to allow for uneven floors – To ensure threshold gaps remain within tolerance and door can operate freely.',
  'Ease and adjust – centre door following above adjustments to bring within tolerance or return to previous state if not allowed for.',
  'Heavy door – 2-person or mechanical lift required',
  'Decoration - To new door and frame only, can touch up architrave as required and minor caulk/touch up around architrave, if exact paint is supplied by client/occupier. Will use quick drying white gloss on woodwork only if no paint supplied.',
  '*Note - Oil based paint will need to be left ajar for several hours afterwards, we can employ tricks of the trade that may mitigate issues if door closed but will still need a minimum couple of hours ajar, this has not been allowed for so if no one present will need to close door.',
  'Supply and install intumescent/cold smoke seals',
  'Compliance – Final calibration of all gaps and closing speeds to ensure the door is fully compliant (for the aspects quoted only) with current standards and within legal safety tolerances as required/feasible.',
].join('\n');

/** True when a quote line is a door / doorset replacement. */
export const isReplacementLine = (it: any): boolean => {
  if (!it || typeof it !== 'object') return false;
  if (it.bundle_tag === 'new_door' || it.defect_type === 'new_door' || it.failure_key === 'new_door') return true;
  const text = `${it.component || ''} ${it.description || ''}`;
  return /replacement door|doorset replacement|door(set)? replacement|new .*fire door leaf|replace (the )?door(set)?/i.test(text);
};

/** Does this quote need the scope block at all? */
export const quoteHasReplacement = (quote: any): boolean =>
  Array.isArray(quote?.items) && quote.items.some(isReplacementLine);

/** Door references (or ids) on this quote that are being replaced. */
export const replacementDoorRefs = (quote: any): string[] => {
  const refs: string[] = [];
  for (const it of (Array.isArray(quote?.items) ? quote.items : [])) {
    if (!isReplacementLine(it)) continue;
    const ref = String(it.door_reference || it.door_id || '').trim();
    if (ref && !refs.includes(ref)) refs.push(ref);
  }
  return refs;
};

/**
 * The scope wording as individual lines. Company override first
 * (`maintenanceCompany.quote_replacement_scope`), else the standard text.
 */
export const replacementScopeLines = (maintenanceCompany?: any): string[] => {
  const custom = String(maintenanceCompany?.quote_replacement_scope || '').trim();
  const src = custom || REPLACEMENT_SCOPE_DEFAULT;
  return src
    .split('\n')
    .map((l) => l.replace(/^[•\-–*]\s*(?!Note)/, '').trim())
    .filter(Boolean);
};

/** Bold the "Heading – detail" lead-in when a line has one. */
export const splitScopeLine = (line: string): { lead: string; rest: string } => {
  const m = line.match(/^([^–\-:]{3,60}?)\s*(–|-|:)\s+(.*)$/);
  if (m && !line.startsWith('*')) return { lead: m[1].trim(), rest: m[3].trim() };
  return { lead: '', rest: line };
};

/** Iter-193/194 — default client-facing text of the per-door post-work inspection line. */
export const REINSPECTION_WORDING_DEFAULT = 'Fire door inspection and compliance report issued upon completion';
